import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { BrandPanel } from "@/components/auth/BrandPanel";
import { apiErrorMessage, getRememberedEmail, setRememberedEmail } from "@/lib/api";
import {
  PRODUCT_NAME, APP_VERSION_LABEL, TRUST_STRIP, LOGO_SRC,
} from "@/lib/brand";
import {
  Mail, Lock, Eye, EyeOff, Building2, ArrowRight, Loader2, ShieldCheck,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState(getRememberedEmail());
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [remember, setRemember] = useState(!!getRememberedEmail());
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, { mfaCode: mfaCode || undefined, tenant: tenant || undefined, remember });
      setRememberedEmail(remember ? email : "");
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.headers?.["x-mfa-required"] || /MFA code required/i.test(String(detail))) {
        setMfaRequired(true);
        setError(t("auth.mfaPrompt", "Enter your MFA code to continue."));
      } else {
        setError(apiErrorMessage(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white pl-11 pr-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-[#118D89]/30 focus:border-[#118D89] transition-colors";

  return (
    <div className="min-h-screen flex bg-white">
      <BrandPanel />

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8 relative">
        {/* Trusted badge */}
        <div className="absolute top-5 right-5 hidden sm:flex items-center gap-2 rounded-full bg-[#E3F3F2] text-[#0E7470] text-xs font-semibold px-3.5 py-1.5">
          <ShieldCheck className="h-4 w-4" /> {t("auth.trusted", "Trusted. Secure. Compliant.")}
        </div>

        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          {/* Mobile brand header */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm">
              <img src={LOGO_SRC} alt="REASON" className="h-9 w-9 object-contain" />
            </div>
            <span className="font-extrabold text-lg text-slate-900">{PRODUCT_NAME}</span>
          </div>

          <h1 className="text-3xl font-extrabold text-slate-900">{t("auth.welcome", "Welcome Back")}</h1>
          <p className="text-slate-500 text-sm mt-2 mb-7">
            {t("auth.loginLead", "Sign in to access your")}{" "}
            <span className="text-[#118D89] font-semibold">ConnectIQ™</span>{" "}
            {t("auth.loginTrail", "Control Plane")}
          </p>

          {error && (
            <div className="mb-5 text-sm rounded-xl bg-red-50 text-red-700 px-4 py-3 border border-red-100" data-testid="login-error">
              {error}
            </div>
          )}

          {/* Organization (optional, multi-tenant) */}
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            {t("auth.organization", "Organization")}{" "}
            <span className="text-slate-400 font-normal">({t("auth.optional", "optional")})</span>
          </label>
          <div className="relative mb-4">
            <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
            <input
              value={tenant} onChange={(e) => setTenant(e.target.value)}
              className={inputCls} placeholder={t("auth.orgPlaceholder", "Your organization / tenant code")}
              data-testid="login-tenant" autoComplete="organization"
            />
          </div>

          {/* Email */}
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.email", "Email Address")}</label>
          <div className="relative mb-4">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} placeholder="you@company.com" data-testid="login-email" autoComplete="email"
            />
          </div>

          {/* Password */}
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.password", "Password")}</label>
          <div className="relative mb-4">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
            <input
              type={showPass ? "text" : "password"} required value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputCls} pr-11`} placeholder={t("auth.passwordPlaceholder", "Enter your password")}
              data-testid="login-password" autoComplete="current-password"
            />
            <button
              type="button" onClick={() => setShowPass((s) => !s)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="login-toggle-password" aria-label="Toggle password visibility"
            >
              {showPass ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
            </button>
          </div>

          {mfaRequired && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.mfa", "Authentication code")}</label>
              <input
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-[#118D89]/30 tracking-[0.3em] font-mono"
                placeholder="123456" data-testid="login-mfa"
              />
            </div>
          )}

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between mb-6">
            <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="login-remember-label">
              <input
                type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                className="h-[18px] w-[18px] rounded border-slate-300 text-[#118D89] focus:ring-[#118D89]/40 accent-[#118D89]"
                data-testid="login-remember"
              />
              <span className="text-sm text-slate-600">{t("auth.rememberMe", "Remember me")}</span>
            </label>
            <Link to="/forgot-password" className="text-sm font-semibold text-[#118D89] hover:underline" data-testid="login-forgot-link">
              {t("auth.forgot", "Forgot Password?")}
            </Link>
          </div>

          {/* Sign in */}
          <button
            type="submit" disabled={loading}
            className="w-full bg-[#0E8C84] hover:bg-[#0c7c74] text-white rounded-xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 shadow-sm"
            data-testid="login-submit"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? t("auth.signingIn", "Signing in…") : t("auth.signIn", "Sign in")}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </button>

          {/* Language + version */}
          <div className="flex items-center justify-between mt-5">
            <LanguageSwitcher />
            <span className="text-xs font-semibold text-slate-400" data-testid="login-version">{APP_VERSION_LABEL}</span>
          </div>

          <div className="border-t border-slate-100 my-6" />

          {/* Trust strip */}
          <div className="grid grid-cols-3 gap-3">
            {TRUST_STRIP.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="text-center">
                  <Icon className="h-5 w-5 mx-auto text-[#118D89]" strokeWidth={1.8} />
                  <div className="text-[11px] font-bold text-slate-700 mt-1.5">{s.title}</div>
                  <div className="text-[10px] text-slate-400">{s.desc}</div>
                </div>
              );
            })}
          </div>

          {/* Legal */}
          <div className="flex items-center justify-center gap-3 mt-6 text-xs text-slate-400">
            <Link to="/privacy-policy" className="hover:text-slate-600" data-testid="login-privacy-link">
              {t("auth.privacy", "Privacy Policy")}
            </Link>
            <span>·</span>
            <Link to="/terms-of-service" className="hover:text-slate-600" data-testid="login-terms-link">
              {t("auth.terms", "Terms of Service")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
